import { Building2, Bed, Scissors, AlertTriangle, CreditCard, Clock } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface ServiceItem {
  description: string;
  date?: string;
  sacCode?: string;
  rate: number;
  qty: number;
  amount: number;
}

interface Props {
  data: {
    documentSubtype: 'OPD_REGISTRATION' | 'OPD_INVOICE' | 'IPD_ADMISSION' | 'IPD_DAILY_BILL' | 'BED_WARD_INVOICE' | 'NURSING_INVOICE' | 'DOCTOR_VISIT_INVOICE' | 'OT_BOOKING' | 'OT_PROCEDURE_INVOICE' | 'SURGERY_CERTIFICATE' | 'ER_REGISTRATION' | 'ER_INVOICE' | 'DISCHARGE_SUMMARY' | 'FINAL_BILL' | 'DEPOSIT_RECEIPT' | 'REFUND_RECEIPT' | 'LAMA_DAMA_TRANSFER';
    registrationId?: string;
    admissionId?: string;
    opdId?: string;
    erId?: string;
    otId?: string;
    bedId?: string;
    wardName?: string;
    roomNo?: string;
    admissionDate?: string;
    dischargeDate?: string;
    dischargeType?: string;
    doctorName: string;
    doctorNhcId?: string;
    nurseName?: string;
    nurseNhcId?: string;
    diagnosis?: string;
    procedureName?: string;
    procedureDate?: string;
    services: ServiceItem[];
    subtotal: number;
    discount?: number;
    gst?: number;
    insuranceCovered?: number;
    patientPayable: number;
    insurerPayable?: number;
    amountPaid: number;
    balanceDue?: number;
    depositAmount?: number;
    refundAmount?: number;
    paymentMode?: string;
    paymentStatus: string;
    preAuthId?: string;
    claimId?: string;
    notes?: string[];
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

const SUBTYPE_LABELS: Record<string, { label: string; icon: typeof Building2 }> = {
  OPD_REGISTRATION: { label: 'OPD Registration Receipt', icon: Building2 },
  OPD_INVOICE: { label: 'OPD Consultation Invoice', icon: Building2 },
  IPD_ADMISSION: { label: 'IPD Admission Receipt', icon: Bed },
  IPD_DAILY_BILL: { label: 'IPD Daily Bill', icon: Bed },
  BED_WARD_INVOICE: { label: 'Bed/Ward/Room Invoice', icon: Bed },
  NURSING_INVOICE: { label: 'Nursing Charges Invoice', icon: Building2 },
  DOCTOR_VISIT_INVOICE: { label: 'Doctor Visit Charges Invoice', icon: Building2 },
  OT_BOOKING: { label: 'OT Booking Receipt', icon: Scissors },
  OT_PROCEDURE_INVOICE: { label: 'OT/Procedure Invoice', icon: Scissors },
  SURGERY_CERTIFICATE: { label: 'Surgery/Procedure Certificate', icon: Scissors },
  ER_REGISTRATION: { label: 'Emergency Registration Receipt', icon: AlertTriangle },
  ER_INVOICE: { label: 'Emergency Treatment Invoice', icon: AlertTriangle },
  DISCHARGE_SUMMARY: { label: 'Discharge Summary', icon: Building2 },
  FINAL_BILL: { label: 'Final Hospital Bill', icon: CreditCard },
  DEPOSIT_RECEIPT: { label: 'Deposit Receipt', icon: CreditCard },
  REFUND_RECEIPT: { label: 'Refund Receipt', icon: CreditCard },
  LAMA_DAMA_TRANSFER: { label: 'LAMA/DAMA/Transfer Document', icon: AlertTriangle },
};

export default function HospitalDocumentTemplate({ data }: Props) {
  const cfg = SUBTYPE_LABELS[data.documentSubtype] || { label: 'Hospital Document', icon: Building2 };
  const Icon = cfg.icon;

  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-gray-700" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">{cfg.label}</span>
      </div>

      {/* IDs & Meta */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        {data.admissionId && <div><span className="text-gray-500">Admission ID:</span> <span className="font-mono text-gray-800">{data.admissionId}</span></div>}
        {data.opdId && <div><span className="text-gray-500">OPD ID:</span> <span className="font-mono text-gray-800">{data.opdId}</span></div>}
        {data.erId && <div><span className="text-gray-500">ER ID:</span> <span className="font-mono text-gray-800">{data.erId}</span></div>}
        {data.otId && <div><span className="text-gray-500">OT ID:</span> <span className="font-mono text-gray-800">{data.otId}</span></div>}
        {data.registrationId && <div><span className="text-gray-500">Reg. ID:</span> <span className="font-mono text-gray-800">{data.registrationId}</span></div>}
        <div><span className="text-gray-500">Doctor:</span> <span className="text-gray-800 font-medium">{data.doctorName}</span></div>
        {data.doctorNhcId && <div><span className="text-gray-500">Doctor NHC:</span> <span className="font-mono text-gray-700">{data.doctorNhcId}</span></div>}
        {data.nurseName && <div><span className="text-gray-500">Nurse:</span> <span className="text-gray-800">{data.nurseName}</span></div>}
        {data.bedId && <div><span className="text-gray-500">Bed:</span> <span className="text-gray-800">{data.bedId}</span></div>}
        {data.wardName && <div><span className="text-gray-500">Ward:</span> <span className="text-gray-800">{data.wardName}</span></div>}
        {data.roomNo && <div><span className="text-gray-500">Room:</span> <span className="text-gray-800">{data.roomNo}</span></div>}
        {data.admissionDate && <div><span className="text-gray-500">Admission:</span> <span className="text-gray-800">{data.admissionDate}</span></div>}
        {data.dischargeDate && <div><span className="text-gray-500">Discharge:</span> <span className="text-gray-800">{data.dischargeDate}</span></div>}
        {data.dischargeType && <div><span className="text-gray-500">Discharge Type:</span> <span className="text-gray-800 font-medium">{data.dischargeType}</span></div>}
        {data.preAuthId && <div><span className="text-gray-500">Pre-Auth:</span> <span className="font-mono text-gray-700">{data.preAuthId}</span></div>}
        {data.claimId && <div><span className="text-gray-500">Claim ID:</span> <span className="font-mono text-gray-700">{data.claimId}</span></div>}
      </div>

      {data.diagnosis && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px]">
          <p className="text-[8px] font-bold uppercase text-gray-500 mb-0.5">Diagnosis / Procedure</p>
          <p className="text-gray-800 font-medium">{data.diagnosis}</p>
          {data.procedureName && <p className="text-gray-700 mt-0.5">Procedure: {data.procedureName} {data.procedureDate && `(${data.procedureDate})`}</p>}
        </div>
      )}

      {/* Services Table */}
      {data.services.length > 0 && (
        <div>
          <p className="text-[8px] font-bold uppercase tracking-wider text-gray-500 mb-1">Service Details</p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[9px]">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Service</th>
                  {data.services.some(s => s.sacCode) && <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300 w-14">SAC</th>}
                  {data.services.some(s => s.date) && <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Date</th>}
                  <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Rate</th>
                  <th className="text-center font-bold text-gray-700 py-1 px-1.5 border border-gray-300 w-10">Qty</th>
                  <th className="text-right font-bold text-gray-700 py-1 px-1.5 border border-gray-300">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.services.map((s, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="py-1 px-1.5 border border-gray-200 text-gray-800 font-medium">{s.description}</td>
                    {data.services.some(x => x.sacCode) && <td className="py-1 px-1.5 border border-gray-200 text-gray-500 font-mono text-[8px]">{s.sacCode || '—'}</td>}
                    {data.services.some(x => x.date) && <td className="py-1 px-1.5 border border-gray-200 text-gray-600 text-[8px]">{s.date || '—'}</td>}
                    <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-right font-mono">{fmt(s.rate)}</td>
                    <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-center">{s.qty}</td>
                    <td className="py-1 px-1.5 border border-gray-200 text-gray-800 text-right font-mono font-medium">{fmt(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Totals */}
      <div className="flex justify-end">
        <div className="w-60 bg-gray-50 border border-gray-200 rounded text-[9px]">
          <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Subtotal</span><span className="font-mono text-gray-800">{fmt(data.subtotal)}</span></div>
          {(data.discount ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Discount</span><span className="font-mono text-green-700">- {fmt(data.discount!)}</span></div>}
          {(data.gst ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">GST</span><span className="font-mono text-gray-800">{fmt(data.gst!)}</span></div>}
          {(data.insuranceCovered ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Insurance</span><span className="font-mono text-blue-700">- {fmt(data.insuranceCovered!)}</span></div>}
          <div className="flex justify-between py-1 px-2.5 bg-gray-100 font-bold border-b border-gray-300"><span className="text-gray-800">Patient Payable</span><span className="font-mono text-gray-900">{fmt(data.patientPayable)}</span></div>
          {(data.insurerPayable ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Insurer Payable</span><span className="font-mono text-blue-700">{fmt(data.insurerPayable!)}</span></div>}
          <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Paid</span><span className="font-mono text-green-700">{fmt(data.amountPaid)}</span></div>
          {(data.depositAmount ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Deposit</span><span className="font-mono text-gray-800">{fmt(data.depositAmount!)}</span></div>}
          {(data.refundAmount ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Refund</span><span className="font-mono text-amber-700">{fmt(data.refundAmount!)}</span></div>}
          {(data.balanceDue ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 font-bold"><span className="text-gray-700">Due</span><span className="font-mono text-red-700">{fmt(data.balanceDue!)}</span></div>}
        </div>
      </div>

      {data.paymentMode && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px] flex items-center gap-2">
          <CreditCard className="w-3 h-3 text-gray-500" />
          <span className="text-gray-500">Payment:</span>
          <span className="text-gray-800">{data.paymentMode}</span>
          <span className="text-gray-400">·</span>
          <span className={`font-bold ${data.paymentStatus === 'PAID' ? 'text-green-700' : 'text-amber-700'}`}>{data.paymentStatus}</span>
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      {data.documentSubtype === 'LAMA_DAMA_TRANSFER' && (
        <div className="bg-red-50 border border-red-200 rounded px-3 py-1.5 text-[9px] text-red-800 flex items-center gap-1.5">
          <AlertTriangle className="w-3 h-3" />
          <span>This is a placeholder document. LAMA/DAMA/Transfer discharge documents require additional legal compliance review.</span>
        </div>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
